import { useState } from 'react';
import { useProducts, useCreateProduct, useUpdateProduct, useDeleteProduct } from '../hooks/useProducts';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createProductSchema, type Product, type CreateProductInput } from '@aigftpwatch/shared';
import { Table } from '../components/ui/Table';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { PlusIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';

const metalOptions = [
  { value: 'Gold', label: 'Gold' },
  { value: 'Silver', label: 'Silver' },
  { value: 'Platinum', label: 'Platinum' },
  { value: 'Palladium', label: 'Palladium' },
  { value: 'Other', label: 'Other' },
];

export function ProductCatalogPage() {
  const [filterMetal, setFilterMetal] = useState<string>('');
  const { data: products, isLoading } = useProducts(filterMetal || undefined);
  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();
  const deleteMutation = useDeleteProduct();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateProductInput>({
    resolver: zodResolver(createProductSchema) as never,
  });

  function openNew() {
    setEditing(null);
    reset({ product_code: '', description: '', metal_type: 'Gold', weight_oz: undefined });
    setModalOpen(true);
  }

  function openEdit(row: Product) {
    setEditing(row);
    reset({
      product_code: row.product_code,
      description: row.description,
      metal_type: row.metal_type,
      weight_oz: row.weight_oz ?? undefined,
    });
    setModalOpen(true);
  }

  async function onSubmit(data: CreateProductInput) {
    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, ...data });
        toast.success('Product updated');
      } else {
        await createMutation.mutateAsync(data);
        toast.success('Product created');
      }
      setModalOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save product');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <p className="text-sm text-silver-500">{products?.length ?? 0} products</p>
          <select
            value={filterMetal}
            onChange={(e) => setFilterMetal(e.target.value)}
            className="rounded-lg border border-silver-300 px-3 py-1.5 text-sm"
          >
            <option value="">All Metals</option>
            {metalOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <Button onClick={openNew}><PlusIcon className="h-4 w-4" /> Add Product</Button>
      </div>

      <Card padding={false}>
        <Table<Product>
          loading={isLoading}
          data={products ?? []}
          onRowClick={openEdit}
          columns={[
            { key: 'product_code', header: 'Code' },
            { key: 'description', header: 'Description' },
            { key: 'metal_type', header: 'Metal', render: (r) => <Badge color="gold">{r.metal_type}</Badge> },
            { key: 'weight_oz', header: 'Weight (oz)', render: (r) => r.weight_oz ? String(r.weight_oz) : '-' },
            { key: 'is_active', header: 'Status', render: (r) => <Badge>{r.is_active ? 'active' : 'inactive'}</Badge> },
          ]}
        />
      </Card>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Product' : 'New Product'}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input label="Product Code" {...register('product_code')} error={errors.product_code?.message} />
          <Input label="Description" {...register('description')} error={errors.description?.message} />
          <Select label="Metal Type" options={metalOptions} {...register('metal_type')} error={errors.metal_type?.message} />
          <Input label="Weight (oz)" type="number" step="any" {...register('weight_oz', { valueAsNumber: true })} error={errors.weight_oz?.message} />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={createMutation.isPending || updateMutation.isPending}>
              {editing ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
